/**
 * FSM 远期房态 futureRoomStatusList：仅拉数并写入本地缓存（2 分钟内存 + localStorage）
 * 供远期房态后续展示「已占用房量」「可售房量」使用，与现有 getAliasPriceQuantity 数据并存
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { getFutureRoomStatusListFromThirdParty, } from '../../service/storeRevenueApi';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from './storeRevenueLocalStorage';
var TTL_MS = 2 * 60 * 1000; // 2 分钟
var memoryCache = new Map();
var inFlight = new Map();
function cacheKey(hotelId) {
    return "futureRoomStatusList:".concat(String(hotelId));
}
function isValid(entry) {
    return Date.now() - entry.fetchedAt < TTL_MS;
}
function isFsmList(v) {
    return Array.isArray(v);
}
/** 从 localStorage 恢复（若有且在 TTL 内）并写入内存缓存 */
function hydrateFromLocalStorage(hotelId) {
    var _a;
    var stored = (_a = getStoreFromLocalStorage(hotelId)) === null || _a === void 0 ? void 0 : _a.futureRoomStatusList;
    if (!(stored === null || stored === void 0 ? void 0 : stored.data) || typeof stored.fetchedAt !== 'number')
        return null;
    if (Date.now() - stored.fetchedAt > TTL_MS)
        return null;
    if (!isFsmList(stored.data))
        return null;
    memoryCache.set(cacheKey(hotelId), { data: stored.data, fetchedAt: stored.fetchedAt });
    return stored.data;
}
/**
 * 获取远期房态列表：优先内存缓存（2 分钟），其次 localStorage（同 TTL），再直调 FSM futureRoomStatusList 并写缓存
 * 时间范围为「今天」至「今天+6天」（中国时区）
 */
export function getFutureRoomStatusListWithCache(hotelId) {
    return __awaiter(this, void 0, Promise, function () {
        var key, entry, fromLocal, existing, promise;
        var _this = this;
        return __generator(this, function (_a) {
            key = cacheKey(hotelId);
            entry = memoryCache.get(key);
            if (entry && isValid(entry))
                return [2 /*return*/, entry.data];
            fromLocal = hydrateFromLocalStorage(hotelId);
            if (fromLocal != null)
                return [2 /*return*/, fromLocal];
            existing = inFlight.get(key);
            if (existing)
                return [2 /*return*/, existing];
            promise = (function () { return __awaiter(_this, void 0, Promise, function () {
                var data, fetchedAt;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, getFutureRoomStatusListFromThirdParty(hotelId)];
                        case 1:
                            data = _a.sent();
                            fetchedAt = Date.now();
                            memoryCache.set(key, { data: data, fetchedAt: fetchedAt });
                            setStoreSliceInLocalStorage(hotelId, 'futureRoomStatusList', { data: data, fetchedAt: fetchedAt });
                            return [2 /*return*/, data];
                    }
                });
            }); })().finally(function () {
                inFlight.delete(key);
            });
            inFlight.set(key, promise);
            return [2 /*return*/, promise];
        });
    });
}
/** 仅从缓存读取（不发起请求），用于图表等消费方 */
export function getFutureRoomStatusListFromCache(hotelId) {
    var key = cacheKey(hotelId);
    var entry = memoryCache.get(key);
    if (entry && isValid(entry))
        return entry.data;
    var fromLocal = hydrateFromLocalStorage(hotelId);
    return fromLocal;
}
export function clearFutureRoomStatusCache(hotelId) {
    memoryCache.delete(cacheKey(hotelId));
    inFlight.delete(cacheKey(hotelId));
}
